/*
 *  Finance — the agency ledger.
 *
 *  The page is a month of the finance log: closed-deal commissions as money
 *  in, expenses as money out, one chronological list. Income lines are
 *  derived live from deals (GET /api/finance/log assembles both sides), so
 *  they carry no ledger row of their own and are read-only here — a
 *  commission is edited on the Deals board.
 *
 *  A deal's commission and the salary payouts booked against it render as ONE
 *  group, headed by the deal with its commission / paid-out / kept figures.
 *  The grouping is done by the server (`group_key` on each line, headers in
 *  `groups`), so a group is never torn across a page: `total` counts GROUPS
 *  AND STANDALONE LINES, while `line_count` counts raw lines. Expenses with no
 *  deal — rent, marketing — stay plain chronological rows.
 *
 *  A payout is routinely booked the month AFTER its deal closed. In that month
 *  the group still forms, and the commission renders as a greyed context line
 *  (`commission_line_included: false`) marked with the month it was booked in.
 *  That line is context only: it is not one of the month's ledger lines and
 *  never reaches the KPI strip, which reads /api/finance/summary separately.
 *
 *  Above the log: a KPI strip sourced from /api/finance/summary, which stays
 *  the authority on totals — income / expenses, then the same pair with the
 *  salary payouts set aside (net income, net expense), then net. Below it:
 *  the per-category breakdown. The category manager and the recurring-template
 *  manager (whose daily tick materialises the monthly costs) are dialogs
 *  opened from the toolbar, since that is where their buttons are.
 *
 *  Categories are the agency's own list (GET /api/finance/categories), not a
 *  fixed set — every label, tint and ordering on this page comes from that
 *  response via `catBy`, so nothing here may hardcode a category again.
 *
 *  Salary payouts are expenses in the `agent_salary` / `team_lead_salary`
 *  buckets, each pointing at the closed deal that earned the commission and
 *  the broker who was paid. The expense modal grows a deal picker for those
 *  two categories and shows the deal's commission plus what has already been
 *  paid against it, so the margin is visible while typing the amount.
 *
 *  Income is accrual-style — booked on the deal's close date, not when cash
 *  arrives — and any month where an FX conversion was unavailable is flagged
 *  rather than silently shown as a partial total.
 */
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api } from "@/api/client";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Modal } from "@/components/Modal";
import { SectionHeader } from "@/components/SectionHeader";
import { Skeleton } from "@/components/Skeleton";
import { fmtAmount, fmtGel, fmtSigned } from "@/lib/money";
import {
  isSalaryCategory,
  roleLabel,
  type Expense,
  type ExpenseCategory,
  type ExpenseCategoryDef,
  type ExpenseCurrency,
  type ExpenseRecurring,
  type FinanceDealListResponse,
  type FinanceDealRef,
  type FinanceLogEntry,
  type FinanceLogGroup,
  type FinanceLogResponse,
  type FinanceSummary,
  type FinanceUserRef,
} from "@/types";

const PAGE_SIZE = 50;

const CURRENCIES: ExpenseCurrency[] = ["GEL", "USD", "EUR"];

/** Category pill tints, keyed by the palette key stored on the category row.
 *  The class strings must be written out literally — Tailwind's JIT only
 *  emits classes it can see in the source, so the DB stores a KEY and this
 *  map owns the CSS. Keep in lockstep with CATEGORY_TONES in
 *  app/models/expense.py, which is what the API validates against. */
const TONE_CLASS: Record<string, string> = {
  slate: "border-slate-500/30 bg-slate-500/10 text-slate-300",
  accent: "border-accent-500/40 bg-accent-500/[0.10] text-accent-300",
  violet: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  sky: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  amber: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  rose: "border-rose-500/30 bg-rose-500/10 text-rose-300",
};
const TONE_KEYS = Object.keys(TONE_CLASS);
const DEFAULT_TONE_CLASS = "border-white/10 bg-white/5 text-slate-400";

function categoryToneClass(tone: string | null | undefined): string {
  return (tone && TONE_CLASS[tone]) || DEFAULT_TONE_CLASS;
}

/** Slug → its category row. Every label and tint on this page goes through
 *  here, and every lookup falls back to the raw slug rather than `undefined`
 *  — a category deleted in another tab must still render its historical rows. */
type CategoryIndex = Map<string, ExpenseCategoryDef>;

function catLabel(catBy: CategoryIndex, slug: string | null | undefined): string {
  if (!slug) return "";
  return catBy.get(slug)?.label ?? slug;
}

function catTone(catBy: CategoryIndex, slug: string | null | undefined): string {
  return categoryToneClass(slug ? catBy.get(slug)?.tone : null);
}

/** Options for a category `<select>`.
 *
 *  Archived categories are hidden — except the row's OWN current value. Drop
 *  that and opening a historical expense whose bucket was later archived would
 *  show the first option as selected, and saving would silently re-file the
 *  row into a category nobody chose.
 */
function categoryOptions(
  cats: ExpenseCategoryDef[],
  current: string,
  { excludeSalary = false }: { excludeSalary?: boolean } = {},
): ExpenseCategoryDef[] {
  return cats.filter((c) => {
    if (c.slug === current) return true;
    if (c.archived) return false;
    if (excludeSalary && isSalaryCategory(c.slug)) return false;
    return true;
  });
}

function thisMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split("-").map(Number);
  const idx = y * 12 + (m - 1) + delta;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, "0")}`;
}

function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

/** `12 Jul` — the ledger's fixed-width date column. */
function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
}

interface ExpenseFormState {
  category: ExpenseCategory;
  amount: string;
  currency: ExpenseCurrency;
  expense_date: string;
  vendor: string;
  description: string;
  deal_id: string;
  payee_user_id: string;
}

interface RecurringFormState {
  name: string;
  category: ExpenseCategory;
  amount: string;
  currency: ExpenseCurrency;
  day_of_month: string;
  start_month: string;
  end_month: string;
  active: boolean;
  vendor: string;
  note: string;
}

type LogFilter = "all" | "income" | "expense";

/** One top-level row of the ledger: a deal group, or a standalone line. */
type LedgerNode =
  | { kind: "entry"; entry: FinanceLogEntry }
  | { kind: "group"; group: FinanceLogGroup; entries: FinanceLogEntry[] };

export function Finance() {
  const [month, setMonth] = useState<string>(thisMonth());
  const [filter, setFilter] = useState<LogFilter>("all");
  const [offset, setOffset] = useState(0);
  // Deal groups the user has folded away. Empty = everything expanded,
  // which is the default: the ledger should read the same as before until
  // someone chooses to collapse something.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [log, setLog] = useState<FinanceLogResponse | null>(null);
  const [recurring, setRecurring] = useState<ExpenseRecurring[] | null>(null);
  const [categories, setCategories] = useState<ExpenseCategoryDef[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [expenseModal, setExpenseModal] = useState<Expense | "new" | null>(null);
  const [recurringModal, setRecurringModal] = useState<
    ExpenseRecurring | "new" | null
  >(null);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [categoryModal, setCategoryModal] = useState<
    ExpenseCategoryDef | "new" | null
  >(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  // The log is filterable and paginated, so a slow response for an earlier
  // filter must not overwrite a newer one.
  const reqSeq = useRef(0);

  const reload = useCallback(async () => {
    const seq = ++reqSeq.current;
    setError(null);
    const qs = new URLSearchParams({
      month,
      limit: String(PAGE_SIZE),
      offset: String(offset),
    });
    if (filter !== "all") qs.set("kind", filter);
    try {
      const [s, l, r, c] = await Promise.all([
        api<FinanceSummary>(`/api/finance/summary?month=${month}`),
        api<FinanceLogResponse>(`/api/finance/log?${qs.toString()}`),
        api<ExpenseRecurring[]>(`/api/finance/recurring`),
        // Ten-odd rows; it rides along with the rest rather than getting its
        // own cache and staleness rules.
        api<ExpenseCategoryDef[]>(`/api/finance/categories`),
      ]);
      if (seq !== reqSeq.current) return;
      setSummary(s);
      setLog(l);
      setRecurring(r);
      setCategories(c);
    } catch (err) {
      if (seq !== reqSeq.current) return;
      setError(
        err instanceof ApiError ? err.message : "Failed to load finance data",
      );
    }
  }, [month, offset, filter]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Changing the month or the filter invalidates the current page position.
  useEffect(() => {
    setOffset(0);
  }, [month, filter]);

  // Collapse state is keyed by deal id, and a different month or page shows a
  // different set of deals — keeping it would silently fold a group the user
  // never touched.
  useEffect(() => {
    setCollapsed(new Set());
  }, [month, filter, offset]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  const cats = useMemo(() => categories ?? [], [categories]);
  const catBy = useMemo<CategoryIndex>(
    () => new Map(cats.map((c) => [c.slug, c])),
    [cats],
  );

  // The server emits a group's lines adjacently and in group order, so one
  // pass over `items` rebuilds the grouping — no sorting or lookup per row.
  const nodes = useMemo<LedgerNode[]>(() => {
    if (!log) return [];
    const groupBy = new Map(log.groups.map((g) => [g.deal_id, g]));
    const out: LedgerNode[] = [];
    for (const entry of log.items) {
      const group = entry.group_key ? groupBy.get(entry.group_key) : undefined;
      if (!group) {
        out.push({ kind: "entry", entry });
        continue;
      }
      const last = out[out.length - 1];
      if (last?.kind === "group" && last.group.deal_id === group.deal_id) {
        last.entries.push(entry);
      } else {
        out.push({ kind: "group", group, entries: [entry] });
      }
    }
    return out;
  }, [log]);

  const monthPoint = summary?.months.find((m) => m.month === month);
  const isCurrentMonth = month === thisMonth();

  const pageStart = log && log.total > 0 ? offset + 1 : 0;
  const pageEnd = log ? Math.min(offset + PAGE_SIZE, log.total) : 0;

  async function afterMutation() {
    setExpenseModal(null);
    setRecurringModal(null);
    setCategoryModal(null);
    await reload();
  }

  /** Move one category up (-1) or down (+1). The API takes a whole ordering
   *  rather than a swap, so a half-applied reorder isn't possible. */
  async function moveCategory(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= cats.length) return;
    const ids = cats.map((c) => c.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    // Optimistic: the reorder is visually obvious and instantly reversible,
    // so waiting a round trip to redraw would just feel laggy.
    const next = [...cats];
    [next[index], next[target]] = [next[target], next[index]];
    setCategories(next);
    try {
      setCategories(
        await api<ExpenseCategoryDef[]>(`/api/finance/categories/reorder`, {
          method: "POST",
          json: { ids },
        }),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to reorder");
      await reload();
    }
  }

  return (
    <div className="w-full space-y-4">
      {/* Toolbar. No page title or subtitle: the sidebar already names the
          page, and the KPI strip below says what it is faster than prose. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <button
            className="icon-btn"
            aria-label="Previous month"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
          >
            <ChevronIcon dir="left" />
          </button>
          <div className="text-[13.5px] font-semibold text-slate-200 min-w-[136px] text-center tabular-nums">
            {monthLabel(month)}
          </div>
          <button
            className="icon-btn disabled:opacity-30"
            aria-label="Next month"
            disabled={isCurrentMonth}
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
          >
            <ChevronIcon dir="right" />
          </button>
        </div>

        {!isCurrentMonth && (
          <button
            className="text-[12px] text-slate-500 hover:text-slate-300"
            onClick={() => setMonth(thisMonth())}
          >
            This month
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            className="btn-secondary !py-2 !px-3 !text-[13px]"
            onClick={() => setCategoriesOpen(true)}
          >
            Categories
            {cats.length > 0 && (
              <span className="tabular-nums text-slate-500">
                {cats.filter((c) => !c.archived).length}
              </span>
            )}
          </button>
          <button
            className="btn-secondary !py-2 !px-3 !text-[13px]"
            onClick={() => setRecurringOpen(true)}
          >
            Recurring
            {recurring && recurring.length > 0 && (
              <span className="tabular-nums text-slate-500">
                {recurring.filter((r) => r.active).length}
              </span>
            )}
          </button>
          <button
            className="btn-primary !py-2 !px-3.5 !text-[13px]"
            onClick={() => setExpenseModal("new")}
          >
            Add expense
          </button>
        </div>
      </div>

      {error && (
        <div className="card p-4 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {error}
        </div>
      )}

      {/* KPI strip — segmented rather than separate cards, so the figures read
          as one equation. Sourced from /summary, which stays the authority on
          totals (the log itself may be truncated).

          The middle pair sets the salary payouts aside: that cut of a
          commission isn't a cost the agency chooses, it scales with income —
          so "what's left of income after paying the brokers" and "what running
          the agency actually costs" are the numbers worth reading. The payout
          cancels out of their difference, which is why Net sits last and reads
          as net income − net expense (identically, income − expenses).

          Separators are drawn by the cells' own top/left borders with the grid
          nudged -1px under an overflow-hidden shell: five tiles wrap to 5 / 3+2
          / 5 across the breakpoints, and `divide-x` would draw the wrong rules
          in every wrapped configuration. */}
      {summary === null ? (
        <Skeleton className="h-[380px] sm:h-[152px] xl:h-[76px] rounded-2xl" />
      ) : (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-5 -mt-px -ml-px">
            <Kpi
              label="Income"
              value={fmtGel(monthPoint?.income_gel ?? null)}
              tone="in"
              hint="Closed-deal commissions"
            />
            <Kpi
              label="Expenses"
              value={fmtGel(monthPoint?.expenses_gel ?? "0")}
              tone="out"
              hint={`${log?.total ?? 0} ledger ${log?.total === 1 ? "line" : "lines"}`}
            />
            <Kpi
              label="Net income"
              value={fmtGel(monthPoint?.net_income_gel ?? null)}
              tone={
                monthPoint?.net_income_gel != null &&
                Number(monthPoint.net_income_gel) < 0
                  ? "out"
                  : "in"
              }
              hint="Income − salary payouts"
            />
            <Kpi
              label="Net expense"
              value={fmtGel(monthPoint?.net_expense_gel ?? "0")}
              tone="out"
              hint="Expenses − salary payouts"
            />
            <Kpi
              label="Net"
              value={fmtGel(monthPoint?.net_gel ?? null)}
              tone={
                monthPoint?.net_gel != null && Number(monthPoint.net_gel) < 0
                  ? "out"
                  : "in"
              }
              hint="Net income − net expense"
            />
          </div>
        </div>
      )}

      {summary && !summary.fx_complete && (
        <div className="text-[12px] text-amber-300 flex items-start gap-1.5">
          <span aria-hidden>⚠</span>
          <span>
            Some amounts couldn't be converted to GEL, so totals are partial.
            Affected lines show their original currency.
          </span>
        </div>
      )}

      {/* The ledger. */}
      <div className="card overflow-hidden">
        <SectionHeader
          title="Finance log"
          subtitle="Closed-deal commissions in, expenses out"
          right={
            <div className="inline-flex h-8 rounded-lg border border-white/10 bg-ink-900/60 p-0.5">
              {(["all", "income", "expense"] as LogFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-2.5 rounded-[6px] text-[12px] font-semibold capitalize transition ${
                    filter === f
                      ? "bg-accent-500/15 text-accent-100"
                      : "text-slate-500 hover:text-slate-300"
                  }`}
                >
                  {f === "all" ? "All" : f === "income" ? "In" : "Out"}
                </button>
              ))}
            </div>
          }
        />

        {log === null ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        ) : log.items.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="text-sm text-slate-400">
              Nothing booked in {monthLabel(month)}.
            </div>
            <div className="text-[12px] text-slate-600 mt-1">
              Closed deals appear here automatically. Use “Add expense” for
              costs and salary payouts.
            </div>
          </div>
        ) : (
          <>
            {/* Mobile: the six-column table is unreadable on a phone, so the
                same lines render as stacked rows below md. */}
            <div className="md:hidden divide-y divide-white/5">
              {nodes.map((n) =>
                n.kind === "group" ? (
                  <DealGroupCard
                    key={`g:${n.group.deal_id}`}
                    group={n.group}
                    entries={n.entries}
                    month={month}
                    catBy={catBy}
                    collapsed={collapsed.has(n.group.deal_id)}
                    onToggle={() => toggleGroup(n.group.deal_id)}
                    onEdit={(e) => openForEdit(e, setExpenseModal, setError)}
                  />
                ) : (
                  <LogCard
                    key={`${n.entry.kind}:${n.entry.id}`}
                    entry={n.entry}
                    catBy={catBy}
                    onEdit={() =>
                      openForEdit(n.entry, setExpenseModal, setError)
                    }
                  />
                ),
              )}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-white/[0.015] border-b border-white/5">
                  <tr>
                    <th className="text-left px-5 py-2.5 font-medium">Date</th>
                    <th className="text-left px-3 py-2.5 font-medium">Type</th>
                    <th className="text-left px-3 py-2.5 font-medium">Detail</th>
                    <th className="text-right px-3 py-2.5 font-medium">Amount</th>
                    <th className="text-right px-3 py-2.5 font-medium">GEL</th>
                    <th className="px-5 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {nodes.map((n) =>
                    n.kind === "group" ? (
                      <DealGroupRows
                        key={`g:${n.group.deal_id}`}
                        group={n.group}
                        entries={n.entries}
                        month={month}
                        catBy={catBy}
                        collapsed={collapsed.has(n.group.deal_id)}
                        onToggle={() => toggleGroup(n.group.deal_id)}
                        onEdit={(e) => openForEdit(e, setExpenseModal, setError)}
                      />
                    ) : (
                      <LogRow
                        key={`${n.entry.kind}:${n.entry.id}`}
                        entry={n.entry}
                        catBy={catBy}
                        onEdit={() =>
                          openForEdit(n.entry, setExpenseModal, setError)
                        }
                      />
                    ),
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-5 py-2.5 border-t border-white/5 text-[11.5px] text-slate-500">
              <span className="tabular-nums">
                {log.total > 0
                  ? `${pageStart}–${pageEnd} of ${log.total} · ${log.line_count} lines`
                  : "0 lines"}
                {log.truncated && " · first 2000 only"}
                {log.fx_missing > 0 && ` · ${log.fx_missing} without FX`}
              </span>
              <div className="flex items-center gap-2">
                <button
                  disabled={offset === 0}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  className="px-2.5 py-1 rounded border border-white/10 disabled:opacity-40 hover:bg-white/5"
                >
                  Prev
                </button>
                <button
                  disabled={pageEnd >= log.total}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  className="px-2.5 py-1 rounded border border-white/10 disabled:opacity-40 hover:bg-white/5"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Where the money went, for the selected month. */}
      {summary && summary.expenses_by_category.length > 0 && (
        <div className="card overflow-hidden">
          <SectionHeader title="By category" />
          <div className="px-5 py-4 space-y-2.5">
            {summary.expenses_by_category.map((c) => {
              const total = Number(summary.expenses_gel) || 1;
              const pct = Math.round((Number(c.total_gel) / total) * 100);
              return (
                <div key={c.category} className="flex items-center gap-3">
                  <div className="w-32 shrink-0 text-[12.5px] text-slate-300 truncate">
                    {catLabel(catBy, c.category)}
                  </div>
                  <div className="flex-1 h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-accent-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="w-10 text-right text-[11px] text-slate-600 tabular-nums">
                    {pct}%
                  </div>
                  <div className="w-24 text-right text-[12.5px] text-slate-200 tabular-nums">
                    {fmtGel(c.total_gel)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Both managers are dialogs, not panels: their buttons live at the top
          of the toolbar, so a panel appended below the breakdown opened
          off-screen from the click that opened it.

          Each list hides while its own form is open, so + Add / Edit swaps the
          dialog body in place rather than stacking a second overlay on the
          first (Modal is a flat z-50). Closing or Escaping the form clears only
          that state, which brings the list back — and `afterMutation` does the
          same after a save, landing on a reloaded list. */}
      {categoriesOpen && categoryModal === null && (
        <CategoryManagerModal
          categories={categories}
          cats={cats}
          onAdd={() => setCategoryModal("new")}
          onEdit={setCategoryModal}
          onMove={moveCategory}
          onClose={() => setCategoriesOpen(false)}
        />
      )}

      {recurringOpen && recurringModal === null && (
        <RecurringManagerModal
          recurring={recurring}
          catBy={catBy}
          onAdd={() => setRecurringModal("new")}
          onEdit={setRecurringModal}
          onClose={() => setRecurringOpen(false)}
        />
      )}

      {expenseModal !== null && (
        <ExpenseModal
          expense={expenseModal === "new" ? null : expenseModal}
          cats={cats}
          defaultMonth={month}
          onClose={() => setExpenseModal(null)}
          onSaved={afterMutation}
        />
      )}
      {recurringModal !== null && (
        <RecurringModal
          recurring={recurringModal === "new" ? null : recurringModal}
          cats={cats}
          defaultMonth={month}
          onClose={() => setRecurringModal(null)}
          onSaved={afterMutation}
        />
      )}
      {categoryModal !== null && (
        <CategoryModal
          cat={categoryModal === "new" ? null : categoryModal}
          onClose={() => setCategoryModal(null)}
          onSaved={afterMutation}
        />
      )}
    </div>
  );
}

/** One row of the category manager. Shows what the category costs the agency
 *  so far, which is also what decides whether it can be deleted or only
 *  archived. */
function CategoryRow({
  cat,
  isFirst,
  isLast,
  onEdit,
  onMove,
}: {
  cat: ExpenseCategoryDef;
  isFirst: boolean;
  isLast: boolean;
  onEdit: () => void;
  onMove: (delta: number) => void;
}) {
  const used = cat.expense_count + cat.recurring_count;
  return (
    <div className="flex items-center gap-3 px-5 py-2.5">
      <span
        className={`inline-flex rounded-md border px-1.5 py-0.5 text-[10px] font-semibold shrink-0 ${categoryToneClass(cat.tone)}`}
      >
        {cat.label}
      </span>
      <div className="flex items-center gap-1.5 min-w-0">
        {cat.archived && (
          <span className="badge border-white/10 bg-white/5 text-slate-400">
            archived
          </span>
        )}
        {cat.protected && (
          <span
            className="badge border-white/10 bg-white/5 text-slate-500"
            title="Salary payouts reference a deal, so this bucket can't be archived or removed."
          >
            built in
          </span>
        )}
      </div>
      <div className="ml-auto flex items-center gap-3 shrink-0">
        <span className="text-[11.5px] text-slate-600 tabular-nums">
          {used === 0
            ? "unused"
            : `${cat.expense_count} expense${cat.expense_count === 1 ? "" : "s"}`}
        </span>
        <div className="flex items-center gap-0.5">
          <button
            className="icon-btn !h-7 !w-7 disabled:opacity-20"
            aria-label="Move up"
            disabled={isFirst}
            onClick={() => onMove(-1)}
          >
            <ArrowIcon dir="up" />
          </button>
          <button
            className="icon-btn !h-7 !w-7 disabled:opacity-20"
            aria-label="Move down"
            disabled={isLast}
            onClick={() => onMove(1)}
          >
            <ArrowIcon dir="down" />
          </button>
        </div>
        <button
          className="text-[12px] text-slate-500 hover:text-slate-200"
          onClick={onEdit}
        >
          Edit
        </button>
      </div>
    </div>
  );
}

function ArrowIcon({ dir }: { dir: "up" | "down" }) {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={dir === "up" ? "M18 15l-6-6-6 6" : "M6 9l6 6 6-6"} />
    </svg>
  );
}

/** The category list as a dialog. Rows bleed to the dialog's edges (the body
 *  has its own padding, which would double up on rows that already carry
 *  `px-5`), so the list reads the same as it did as a card. */
function CategoryManagerModal({
  categories,
  cats,
  onAdd,
  onEdit,
  onMove,
  onClose,
}: {
  /** Null while loading — distinct from an empty list, which can't happen
   *  here since every agency is seeded with the system buckets. */
  categories: ExpenseCategoryDef[] | null;
  cats: ExpenseCategoryDef[];
  onAdd: () => void;
  onEdit: (cat: ExpenseCategoryDef) => void;
  onMove: (index: number, delta: number) => void;
  onClose: () => void;
}) {
  return (
    <Modal
      title="Categories"
      subtitle="Your own cost buckets — rename, recolour, reorder"
      size="lg"
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2 w-full">
          <button
            type="button"
            className="text-[12.5px] text-accent-300 hover:text-accent-200 font-semibold"
            onClick={onAdd}
          >
            + Add category
          </button>
          <button type="button" className="btn-secondary ml-auto" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      {categories === null ? (
        <Skeleton className="h-16" />
      ) : (
        <div className="-mx-5 -my-4 divide-y divide-white/5">
          {cats.map((c, i) => (
            <CategoryRow
              key={c.id}
              cat={c}
              isFirst={i === 0}
              isLast={i === cats.length - 1}
              onEdit={() => onEdit(c)}
              onMove={(delta) => onMove(i, delta)}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

function CategoryModal({
  cat,
  onClose,
  onSaved,
}: {
  cat: ExpenseCategoryDef | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [label, setLabel] = useState(cat?.label ?? "");
  const [tone, setTone] = useState<string>(cat?.tone ?? "slate");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const used = cat ? cat.expense_count + cat.recurring_count : 0;
  // Deleting is only offered when nothing references the category; otherwise
  // archiving is the way out, and the API enforces the same rule.
  const deletable = cat !== null && !cat.protected && used === 0;

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (cat) {
        await api(`/api/finance/categories/${cat.id}`, {
          method: "PATCH",
          json: { label, tone },
        });
      } else {
        await api(`/api/finance/categories`, {
          method: "POST",
          json: { label, tone },
        });
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Save failed");
      setBusy(false);
    }
  }

  async function setArchived(archived: boolean) {
    if (!cat) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/finance/categories/${cat.id}`, {
        method: "PATCH",
        json: { archived },
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed");
      setBusy(false);
    }
  }

  async function remove() {
    if (!cat) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`/api/finance/categories/${cat.id}`, { method: "DELETE" });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Delete failed");
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        title={cat ? "Edit category" : "New category"}
        onClose={onClose}
        footer={
          <div className="flex items-center gap-2 w-full">
            {cat && !cat.protected && (
              <button
                type="button"
                className="text-[12.5px] text-slate-500 hover:text-slate-300 disabled:opacity-40"
                disabled={busy}
                onClick={() => setArchived(!cat.archived)}
              >
                {cat.archived ? "Restore" : "Archive"}
              </button>
            )}
            {deletable && (
              <button
                type="button"
                className="text-[12.5px] text-rose-300 hover:text-rose-200 disabled:opacity-40"
                disabled={busy}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </button>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                form="category-form"
                className="btn-primary"
                disabled={busy || !label.trim()}
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        }
      >
        <form id="category-form" onSubmit={submit} className="space-y-3.5">
          <Field
            label="Name"
            hint={
              cat?.protected
                ? "Renaming is fine — this bucket stays linked to deal payouts however you label it."
                : undefined
            }
          >
            <input
              className="input"
              required
              maxLength={60}
              autoFocus
              placeholder="e.g. Advertising"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>

          <Field label="Colour">
            <div className="flex flex-wrap gap-1.5 pt-1">
              {TONE_KEYS.map((t) => (
                <button
                  key={t}
                  type="button"
                  aria-label={t}
                  aria-pressed={tone === t}
                  onClick={() => setTone(t)}
                  className={`inline-flex rounded-md border px-2.5 py-1 text-[11px] font-semibold capitalize transition-opacity ${TONE_CLASS[t]} ${
                    tone === t ? "ring-1 ring-white/40" : "opacity-60 hover:opacity-100"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </Field>

          {cat && used > 0 && (
            <div className="text-[11.5px] text-slate-600">
              Used by {cat.expense_count} expense
              {cat.expense_count === 1 ? "" : "s"}
              {cat.recurring_count > 0 &&
                ` and ${cat.recurring_count} recurring template${cat.recurring_count === 1 ? "" : "s"}`}
              . Archive it to hide it from the pickers — past rows keep it.
            </div>
          )}
          {err && <div className="text-[12.5px] text-rose-300">{err}</div>}
        </form>
      </Modal>

      {confirmDelete && (
        <ConfirmDialog
          open
          title="Delete category?"
          description="Nothing uses this category, so it can be removed for good."
          confirmLabel="Delete"
          variant="danger"
          onCancel={() => setConfirmDelete(false)}
          onConfirm={remove}
        />
      )}
    </>
  );
}

/** Income lines have no expense row to edit, so this only fires for expenses.
 *  It refetches the row (the log carries a projection, not the full record)
 *  before opening the modal. */
async function openForEdit(
  entry: FinanceLogEntry,
  setExpenseModal: (e: Expense | "new" | null) => void,
  setError: (s: string | null) => void,
) {
  if (!entry.editable) return;
  try {
    // There's no GET /expenses/{id}, so fetch the ledger rows for that exact
    // day — narrow enough that the row is always in the first page.
    const list = await api<{ items: Expense[] }>(
      `/api/finance/expenses?limit=500&from=${entry.date}&to=${entry.date}`,
    );
    const row = list.items.find((x) => x.id === entry.id);
    if (!row) {
      setError("That expense no longer exists.");
      return;
    }
    setExpenseModal(row);
  } catch (e) {
    setError(e instanceof ApiError ? e.message : "Couldn't open that expense");
  }
}

function Kpi({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: string;
  tone: "in" | "out";
  hint: string;
}) {
  // emerald-300 / rose-300 rather than the -400 ramp: only the -300 steps
  // have light-theme overrides in index.css, so -400 would be unreadable on
  // white.
  const toneClass = tone === "in" ? "text-emerald-300" : "text-rose-300";
  // The cell draws its own top/left hairline; the strip's -1px offset pushes
  // the outermost ones under the rounded shell's own border. See the KPI strip.
  return (
    <div className="px-4 py-3 border-t border-l border-white/5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      <div
        className={`text-[22px] font-semibold tabular-nums leading-tight mt-1 ${toneClass}`}
      >
        {value}
      </div>
      <div className="text-[11px] text-slate-600 mt-0.5">{hint}</div>
    </div>
  );
}

/** The label shown in the ledger's Type column. */
function entryTypeLabel(e: FinanceLogEntry, catBy: CategoryIndex): string {
  if (e.kind === "income") return "Commission";
  return e.category ? catLabel(catBy, e.category) : "Expense";
}

function entryTypeTone(e: FinanceLogEntry, catBy: CategoryIndex): string {
  if (e.kind === "income")
    return "border-emerald-500/30 bg-emerald-500/10 text-emerald-300";
  return e.category ? catTone(catBy, e.category) : DEFAULT_TONE_CLASS;
}

/** The Detail column's lead. A salary line already leads with the payee; a
 *  commission line leads with the deal — which inside a group is exactly what
 *  the header just said, so there it leads with the agent instead. */
function entryTitle(e: FinanceLogEntry, grouped: boolean): string {
  if (grouped && e.kind === "income") return e.agent_name || e.title;
  return e.title;
}

/** Secondary line: who/what the money relates to. Inside a group the deal is
 *  already named by the header, so repeating it on every line is noise —
 *  `grouped` drops it (and the subtitle, which is the deal title for salary
 *  lines) rather than forking the component. */
function entryContext(e: FinanceLogEntry, grouped = false): string | null {
  const parts: string[] = [];
  // Grouped income already leads with the agent (see entryTitle).
  if (!grouped && e.kind === "income" && e.agent_name) parts.push(e.agent_name);
  if (!grouped && e.kind === "expense" && e.deal_title) parts.push(e.deal_title);
  if (
    e.subtitle &&
    !parts.includes(e.subtitle) &&
    !(grouped && e.subtitle === e.deal_title)
  ) {
    parts.push(e.subtitle);
  }
  return parts.length ? parts.join(" · ") : null;
}

/** Group toggle caret. Finance's own `ChevronIcon` takes a fixed direction for
 *  the month stepper, so it can't express open/closed. Matches Team.tsx. */
function GroupChevron({ open }: { open: boolean }) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`transition-transform shrink-0 ${open ? "rotate-90" : ""}`}
      aria-hidden
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/** True when the deal closed outside the month being viewed — the only case
 *  where the context line needs to name a date, since inside the month the
 *  date is already obvious from the lines. */
function closedElsewhere(group: FinanceLogGroup, month: string): boolean {
  return group.deal_date.slice(0, 7) !== month;
}

/** The commission / paid / kept summary shared by the table header and the
 *  mobile card, so the two can never drift apart. */
function GroupSummary({ group }: { group: FinanceLogGroup }) {
  return (
    <span className="text-[11px] text-slate-500 tabular-nums">
      <span className="text-emerald-300/90">
        {fmtSigned(group.commission, group.currency, "in")}
      </span>
      {" · paid "}
      <span className="text-rose-300/90">
        {fmtGel(group.paid_out_gel)}
        {!group.paid_out_complete && "+"}
      </span>
      {group.kept_gel !== null && (
        <>
          {" · kept "}
          <span className="text-slate-300 font-semibold">
            {fmtGel(group.kept_gel)}
          </span>
        </>
      )}
      {group.paid_out_beyond_month && (
        <span
          className="text-slate-600"
          title="Includes payouts booked in other months, which are not listed here"
        >
          {" · all-time"}
        </span>
      )}
    </span>
  );
}

/** The greyed commission line shown when the commission is not one of this
 *  page's ledger lines — the deal closed in an earlier month, or the active
 *  filter excluded income. Context only: it is not part of any total. */
function ContextCommissionRow({
  group,
  month,
}: {
  group: FinanceLogGroup;
  month: string;
}) {
  return (
    <tr className="border-t border-white/5 bg-white/[0.012]">
      <td className="px-5 py-2 text-slate-600 font-mono tabular-nums whitespace-nowrap">
        {closedElsewhere(group, month) ? dayLabel(group.deal_date) : ""}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        <span className="inline-flex rounded-md border border-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
          Commission
        </span>
      </td>
      <td className="px-3 py-2 max-w-[340px]">
        <div className="text-[11px] text-slate-600 truncate">
          {closedElsewhere(group, month)
            ? `Booked in ${monthLabel(group.deal_date.slice(0, 7))} — not counted in this month`
            : "Not shown under the current filter"}
        </div>
      </td>
      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-600">
        {fmtSigned(group.commission, group.currency, "in")}
      </td>
      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-slate-600">
        {group.commission_gel === null ? "—" : fmtGel(group.commission_gel)}
      </td>
      <td className="px-5 py-2" />
    </tr>
  );
}

/** A deal group in the desktop table: header row, the greyed context
 *  commission when the real line isn't here, then the member lines. */
function DealGroupRows({
  group,
  entries,
  month,
  catBy,
  collapsed,
  onToggle,
  onEdit,
}: {
  group: FinanceLogGroup;
  entries: FinanceLogEntry[];
  month: string;
  catBy: CategoryIndex;
  collapsed: boolean;
  onToggle: () => void;
  onEdit: (e: FinanceLogEntry) => void;
}) {
  return (
    <>
      <tr className="border-t border-white/5 bg-gradient-to-r from-white/[0.03] to-transparent">
        <td colSpan={6} className="px-5 py-2.5">
          <button
            type="button"
            onClick={onToggle}
            className="w-full flex items-center gap-2 text-left text-slate-300 hover:text-slate-100"
            aria-expanded={!collapsed}
          >
            <GroupChevron open={!collapsed} />
            <span className="inline-flex rounded-md border border-emerald-500/30 bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-300 shrink-0">
              Deal
            </span>
            <span className="text-[12.5px] font-semibold truncate">
              {group.deal_title}
            </span>
            {group.agent_name && (
              <span className="text-[11px] text-slate-600 truncate shrink-0">
                · {group.agent_name}
              </span>
            )}
            <span className="ml-auto pl-3 shrink-0">
              <GroupSummary group={group} />
            </span>
          </button>
        </td>
      </tr>
      {!collapsed && !group.commission_line_included && (
        <ContextCommissionRow group={group} month={month} />
      )}
      {!collapsed &&
        entries.map((e) => (
          <LogRow
            key={`${e.kind}:${e.id}`}
            entry={e}
            catBy={catBy}
            grouped
            onEdit={() => onEdit(e)}
          />
        ))}
    </>
  );
}

/** The same group for the mobile card list. */
function DealGroupCard({
  group,
  entries,
  month,
  catBy,
  collapsed,
  onToggle,
  onEdit,
}: {
  group: FinanceLogGroup;
  entries: FinanceLogEntry[];
  month: string;
  catBy: CategoryIndex;
  collapsed: boolean;
  onToggle: () => void;
  onEdit: (e: FinanceLogEntry) => void;
}) {
  return (
    <div className="px-4 py-3">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg bg-gradient-to-r from-white/[0.04] to-transparent border border-white/10 text-slate-300 hover:border-white/20 transition"
        aria-expanded={!collapsed}
      >
        <GroupChevron open={!collapsed} />
        <span className="min-w-0 flex-1">
          <span className="block text-[12.5px] font-semibold truncate">
            {group.deal_title}
          </span>
          <span className="block mt-0.5">
            <GroupSummary group={group} />
          </span>
        </span>
      </button>
      {!collapsed && !group.commission_line_included && (
        <div className="mt-1 pl-3 border-l border-white/10 text-[11px] text-slate-600">
          Commission {fmtSigned(group.commission, group.currency, "in")}
          {closedElsewhere(group, month)
            ? ` · booked in ${monthLabel(group.deal_date.slice(0, 7))}`
            : " · not shown under the current filter"}
        </div>
      )}
      {!collapsed && (
        <div className="mt-1 pl-3 border-l border-white/10 divide-y divide-white/5">
          {entries.map((e) => (
            <LogCard
              key={`${e.kind}:${e.id}`}
              entry={e}
              catBy={catBy}
              grouped
              onEdit={() => onEdit(e)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LogRow({
  entry,
  catBy,
  onEdit,
  grouped = false,
}: {
  entry: FinanceLogEntry;
  catBy: CategoryIndex;
  onEdit: () => void;
  grouped?: boolean;
}) {
  const signClass =
    entry.direction === "in" ? "text-emerald-300" : "text-rose-300";
  const context = entryContext(entry, grouped);
  return (
    <tr className="border-t border-white/5 hover:bg-white/[0.02]">
      <td
        className={`px-5 py-2.5 text-slate-500 font-mono tabular-nums whitespace-nowrap ${grouped ? "pl-9" : ""}`}
      >
        {dayLabel(entry.date)}
      </td>
      <td className="px-3 py-2.5 whitespace-nowrap">
        <span
          className={`inline-flex rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${entryTypeTone(entry, catBy)}`}
        >
          {entryTypeLabel(entry, catBy)}
        </span>
        {entry.source === "recurring" && (
          <span className="ml-1.5 text-[10px] text-slate-600">auto</span>
        )}
      </td>
      <td className="px-3 py-2.5 max-w-[340px]">
        <div className="text-slate-200 truncate">
          {entryTitle(entry, grouped)}
        </div>
        {context && (
          <div className="text-[11px] text-slate-600 truncate">{context}</div>
        )}
      </td>
      <td
        className={`px-3 py-2.5 text-right tabular-nums whitespace-nowrap font-semibold ${signClass}`}
      >
        {fmtSigned(entry.amount, entry.currency, entry.direction)}
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap text-slate-400">
        {entry.amount_gel === null ? (
          <span title="No FX rate available for this line">—</span>
        ) : (
          fmtGel(entry.amount_gel)
        )}
      </td>
      <td className="px-5 py-2.5 text-right whitespace-nowrap">
        {entry.editable ? (
          <button
            className="text-[12px] text-slate-500 hover:text-slate-200"
            onClick={onEdit}
          >
            Edit
          </button>
        ) : (
          <span
            className="text-[11px] text-slate-700"
            title="Commissions are edited on the Deals board"
          >
            deal
          </span>
        )}
      </td>
    </tr>
  );
}

function LogCard({
  entry,
  catBy,
  onEdit,
  grouped = false,
}: {
  entry: FinanceLogEntry;
  catBy: CategoryIndex;
  onEdit: () => void;
  grouped?: boolean;
}) {
  const signClass =
    entry.direction === "in" ? "text-emerald-300" : "text-rose-300";
  const context = entryContext(entry, grouped);
  return (
    <div className={`flex items-start gap-3 ${grouped ? "px-3 py-2" : "px-4 py-3"}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${entryTypeTone(entry, catBy)}`}
          >
            {entryTypeLabel(entry, catBy)}
          </span>
          <span className="text-[11px] text-slate-600 font-mono tabular-nums">
            {dayLabel(entry.date)}
          </span>
        </div>
        <div className="text-[13px] text-slate-200 mt-1 truncate">
          {entryTitle(entry, grouped)}
        </div>
        {context && (
          <div className="text-[11px] text-slate-600 truncate">{context}</div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className={`text-[13.5px] font-semibold tabular-nums ${signClass}`}>
          {fmtSigned(entry.amount, entry.currency, entry.direction)}
        </div>
        {entry.amount_gel !== null && entry.currency !== "GEL" && (
          <div className="text-[11px] text-slate-600 tabular-nums">
            {fmtGel(entry.amount_gel)}
          </div>
        )}
        {entry.editable && (
          <button
            className="text-[11px] text-slate-500 hover:text-slate-200 mt-0.5"
            onClick={onEdit}
          >
            Edit
          </button>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      {children}
      {hint && <span className="block text-[11px] text-slate-600 mt-1">{hint}</span>}
    </label>
  );
}

function ChevronIcon({ dir }: { dir: "left" | "right" }) {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={dir === "left" ? "M15 18l-6-6 6-6" : "M9 6l6 6-6 6"} />
    </svg>
  );
}

function ExpenseModal({
  expense,
  cats,
  defaultMonth,
  onClose,
  onSaved,
}: {
  expense: Expense | null;
  cats: ExpenseCategoryDef[];
  defaultMonth: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [form, setForm] = useState<ExpenseFormState>({
    // `agent_salary` is safe as the default for a new row: it's one of the two
    // buckets that can never be archived or deleted.
    category: expense?.category ?? "agent_salary",
    amount: expense?.amount ?? "",
    currency: (expense?.currency as ExpenseCurrency) ?? "GEL",
    expense_date: expense?.expense_date ?? `${defaultMonth}-01`,
    vendor: expense?.vendor ?? "",
    description: expense?.description ?? "",
    deal_id: expense?.deal_id ?? "",
    payee_user_id: expense?.payee_user_id ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isSalary = isSalaryCategory(form.category);
  const options = useMemo(
    () => categoryOptions(cats, form.category),
    [cats, form.category],
  );

  // Closed deals for the picker. Loaded lazily — only a salary category needs
  // them, and the list is the same regardless of which salary bucket.
  const [deals, setDeals] = useState<FinanceDealRef[] | null>(null);
  const [dealQuery, setDealQuery] = useState("");
  useEffect(() => {
    if (!isSalary) return;
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const qs = new URLSearchParams({ month: defaultMonth });
        if (dealQuery.trim()) qs.set("q", dealQuery.trim());
        const res = await api<FinanceDealListResponse>(
          `/api/finance/deals?${qs.toString()}`,
        );
        if (alive) setDeals(res.items);
      } catch {
        if (alive) setDeals([]);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [isSalary, dealQuery, defaultMonth]);

  const selectedDeal = useMemo(
    () => deals?.find((d) => d.id === form.deal_id) ?? null,
    [deals, form.deal_id],
  );

  // Who this payout can go to: the deal's broker for an agent salary, that
  // broker's lead brokers for a team-lead salary.
  const payeeOptions: FinanceUserRef[] = useMemo(() => {
    if (!selectedDeal) return [];
    if (form.category === "agent_salary") {
      return selectedDeal.agent ? [selectedDeal.agent] : [];
    }
    return selectedDeal.team_lead_candidates;
  }, [selectedDeal, form.category]);

  // Default the payee to the only sensible choice, and drop a stale one when
  // the deal or the bucket changes.
  useEffect(() => {
    if (!isSalary) return;
    const ids = payeeOptions.map((p) => p.id);
    if (form.payee_user_id && ids.includes(form.payee_user_id)) return;
    setForm((f) => ({
      ...f,
      payee_user_id: ids.length === 1 ? ids[0] : "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payeeOptions, isSalary]);

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    if (isSalary && (!form.deal_id || !form.payee_user_id)) {
      setErr("Pick the deal and who was paid.");
      return;
    }
    setBusy(true);
    setErr(null);
    const body = {
      category: form.category,
      amount: form.amount,
      currency: form.currency,
      expense_date: form.expense_date,
      vendor: form.vendor || null,
      description: form.description || null,
      // Sent as explicit nulls for non-salary rows so a re-categorised payout
      // drops its stale references (the API clears them too).
      deal_id: isSalary ? form.deal_id : null,
      payee_user_id: isSalary ? form.payee_user_id : null,
    };
    try {
      if (expense) {
        await api(`/api/finance/expenses/${expense.id}`, {
          method: "PATCH",
          json: body,
        });
      } else {
        await api(`/api/finance/expenses`, { method: "POST", json: body });
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Save failed");
      setBusy(false);
    }
  }

  async function remove() {
    if (!expense) return;
    await api(`/api/finance/expenses/${expense.id}`, { method: "DELETE" });
    await onSaved();
  }

  return (
    <>
      <Modal
        title={expense ? "Edit expense" : "Add expense"}
        subtitle={
          isSalary
            ? "A payout booked against a closed deal"
            : "An agency operating cost"
        }
        size={isSalary ? "xl" : "lg"}
        onClose={onClose}
      >
        <form id="expense-form" onSubmit={submit} className="space-y-3.5">
          <Field label="Category">
            <select
              className="input"
              value={form.category}
              onChange={(e) =>
                setForm({
                  ...form,
                  category: e.target.value as ExpenseCategory,
                })
              }
            >
              {options.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.label}
                  {c.archived ? " (archived)" : ""}
                </option>
              ))}
            </select>
          </Field>

          {isSalary && (
            <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3 space-y-3">
              <Field label="Deal">
                <input
                  className="input mb-2"
                  value={dealQuery}
                  placeholder="Search closed deals by property or client…"
                  onChange={(e) => setDealQuery(e.target.value)}
                />
                {deals === null ? (
                  <Skeleton className="h-9" />
                ) : (
                  <select
                    className="input"
                    required
                    value={form.deal_id}
                    onChange={(e) =>
                      setForm({ ...form, deal_id: e.target.value })
                    }
                  >
                    <option value="">Select a closed deal…</option>
                    {deals.map((d) => (
                      <option key={d.id} value={d.id}>
                        {dayLabel(d.deal_date)} · {d.title}
                        {d.client_name ? ` · ${d.client_name}` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </Field>

              {deals !== null && deals.length === 0 && (
                <div className="text-[11.5px] text-amber-300">
                  No closed deals in the last few months. A salary payout has
                  to reference one.
                </div>
              )}

              {selectedDeal && <DealContext deal={selectedDeal} />}

              <Field
                label={
                  form.category === "agent_salary" ? "Agent paid" : "Team lead paid"
                }
                hint={
                  payeeOptions.length === 0 && selectedDeal
                    ? form.category === "agent_salary"
                      ? "This deal has no broker assigned — assign one on the Deals board first."
                      : "This deal's broker has no manager on record. Set one in Team, or book this as an agent salary."
                    : undefined
                }
              >
                <select
                  className="input"
                  required
                  value={form.payee_user_id}
                  disabled={!selectedDeal || payeeOptions.length === 0}
                  onChange={(e) =>
                    setForm({ ...form, payee_user_id: e.target.value })
                  }
                >
                  <option value="">
                    {selectedDeal ? "Select…" : "Pick a deal first"}
                  </option>
                  {payeeOptions.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {p.role ? ` — ${roleLabel(p.role)}` : ""}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount">
              <input
                className="input tabular-nums"
                type="number"
                step="0.01"
                min="0"
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Field>
            <Field label="Currency">
              <select
                className="input"
                value={form.currency}
                onChange={(e) =>
                  setForm({
                    ...form,
                    currency: e.target.value as ExpenseCurrency,
                  })
                }
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Date">
            <input
              className="input"
              type="date"
              required
              value={form.expense_date}
              onChange={(e) =>
                setForm({ ...form, expense_date: e.target.value })
              }
            />
          </Field>

          {!isSalary && (
            <Field label="Vendor (optional)">
              <input
                className="input"
                value={form.vendor}
                placeholder="e.g. Magti, landlord…"
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              />
            </Field>
          )}

          <Field
            label="Note (optional)"
            hint={
              isSalary
                ? "Left blank, this is filled in with the payee and deal, so the line still reads correctly if the deal is later deleted."
                : undefined
            }
          >
            <input
              className="input"
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </Field>

          {err && <div className="text-[12.5px] text-rose-300">{err}</div>}
        </form>

        <div className="flex items-center justify-between pt-4">
          {expense ? (
            <button
              type="button"
              className="btn-danger !py-2 !px-3 !text-[13px]"
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
            >
              Delete
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary !py-2 !px-3 !text-[13px]"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="expense-form"
              className="btn-primary !py-2 !px-3.5 !text-[13px]"
              disabled={busy}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this expense?"
        description="The line disappears from the ledger and the month's totals. The audit trail keeps a record."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={remove}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}

/** What the deal earned and what's already been paid against it, so the
 *  margin is visible while typing the amount. Read-only context — the figure
 *  is never computed for you. */
function DealContext({ deal }: { deal: FinanceDealRef }) {
  const commissionGel = deal.commission_gel;
  const paid = Number(deal.paid_out_gel) || 0;
  const remaining =
    commissionGel !== null ? Number(commissionGel) - paid : null;
  return (
    <div className="rounded-lg border border-white/5 bg-ink-900/60 px-3 py-2.5 grid grid-cols-3 gap-2">
      <Stat
        label="Commission"
        value={fmtAmount(deal.commission, deal.currency)}
        sub={
          deal.currency !== "GEL" && commissionGel !== null
            ? fmtGel(commissionGel)
            : undefined
        }
      />
      <Stat
        label={deal.paid_out_complete ? "Already paid" : "Already paid (min.)"}
        value={fmtGel(deal.paid_out_gel)}
      />
      <Stat
        label="Left"
        value={remaining === null ? "—" : fmtGel(String(remaining))}
        tone={remaining !== null && remaining < 0 ? "warn" : undefined}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "warn";
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </div>
      <div
        className={`text-[13px] font-semibold tabular-nums ${
          tone === "warn" ? "text-amber-300" : "text-slate-100"
        }`}
      >
        {value}
      </div>
      {sub && <div className="text-[10.5px] text-slate-600 tabular-nums">{sub}</div>}
    </div>
  );
}

/** The recurring templates as a dialog — same edge-to-edge list treatment as
 *  the category manager. */
function RecurringManagerModal({
  recurring,
  catBy,
  onAdd,
  onEdit,
  onClose,
}: {
  recurring: ExpenseRecurring[] | null; // null while loading
  catBy: CategoryIndex;
  onAdd: () => void;
  onEdit: (r: ExpenseRecurring) => void;
  onClose: () => void;
}) {
  return (
    <Modal
      title="Recurring monthly costs"
      subtitle="Posted automatically each month"
      size="lg"
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2 w-full">
          <button
            type="button"
            className="text-[12.5px] text-accent-300 hover:text-accent-200 font-semibold"
            onClick={onAdd}
          >
            + Add recurring cost
          </button>
          <button type="button" className="btn-secondary ml-auto" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      {recurring === null ? (
        <Skeleton className="h-16" />
      ) : recurring.length === 0 ? (
        <div className="py-6 text-center text-[12.5px] text-slate-500">
          Nothing recurring yet. Add rent, internet, base salaries… and they'll
          post on their own each month.
        </div>
      ) : (
        <div className="-mx-5 -my-4 divide-y divide-white/5">
          {recurring.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-5 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-slate-200 flex items-center gap-2">
                  <span className="truncate">{r.name}</span>
                  {!r.active && (
                    <span className="badge border-white/10 bg-white/5 text-slate-400">
                      paused
                    </span>
                  )}
                </div>
                <div className="text-[11.5px] text-slate-500 tabular-nums">
                  {catLabel(catBy, r.category)} · day {r.day_of_month} ·{" "}
                  {fmtAmount(r.amount, r.currency)}/mo
                </div>
              </div>
              <button
                className="text-[12px] text-slate-500 hover:text-slate-200"
                onClick={() => onEdit(r)}
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function RecurringModal({
  recurring,
  cats,
  defaultMonth,
  onClose,
  onSaved,
}: {
  recurring: ExpenseRecurring | null;
  cats: ExpenseCategoryDef[];
  defaultMonth: string;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  // Unlike the expense modal there is no safe hardcoded default here — every
  // category a template may use is archivable — so fall back to the first
  // offered option, and refuse to submit if somehow there is none.
  const [form, setForm] = useState<RecurringFormState>({
    name: recurring?.name ?? "",
    category:
      recurring?.category ??
      categoryOptions(cats, "", { excludeSalary: true })[0]?.slug ??
      "",
    amount: recurring?.amount ?? "",
    currency: (recurring?.currency as ExpenseCurrency) ?? "GEL",
    day_of_month: String(recurring?.day_of_month ?? 1),
    start_month: recurring?.start_month?.slice(0, 7) ?? defaultMonth,
    end_month: recurring?.end_month?.slice(0, 7) ?? "",
    active: recurring?.active ?? true,
    vendor: recurring?.vendor ?? "",
    note: recurring?.note ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const options = useMemo(
    () => categoryOptions(cats, form.category, { excludeSalary: true }),
    [cats, form.category],
  );

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    if (!form.category) {
      setErr("Add a category first — a recurring cost needs one.");
      return;
    }
    setBusy(true);
    setErr(null);
    const body = {
      name: form.name,
      category: form.category,
      amount: form.amount,
      currency: form.currency,
      day_of_month: Number(form.day_of_month),
      start_month: `${form.start_month}-01`,
      end_month: form.end_month ? `${form.end_month}-01` : null,
      active: form.active,
      vendor: form.vendor || null,
      note: form.note || null,
    };
    try {
      if (recurring) {
        await api(`/api/finance/recurring/${recurring.id}`, {
          method: "PATCH",
          json: body,
        });
      } else {
        await api(`/api/finance/recurring`, { method: "POST", json: body });
      }
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Save failed");
      setBusy(false);
    }
  }

  async function remove() {
    if (!recurring) return;
    await api(`/api/finance/recurring/${recurring.id}`, { method: "DELETE" });
    await onSaved();
  }

  async function runNow() {
    if (!recurring) return;
    setBusy(true);
    try {
      await api(`/api/finance/recurring/${recurring.id}/run-now`, {
        method: "POST",
      });
      await onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Run failed");
      setBusy(false);
    }
  }

  return (
    <>
      <Modal
        title={recurring ? "Edit recurring cost" : "Add recurring cost"}
        subtitle="Posts one expense per month, automatically"
        size="lg"
        onClose={onClose}
      >
        <form id="recurring-form" onSubmit={submit} className="space-y-3.5">
          <Field label="Name">
            <input
              className="input"
              required
              placeholder="e.g. Office rent"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Category"
              hint="Salary payouts aren't available here — they reference a specific deal, so they're added as one-off expenses."
            >
              <select
                className="input"
                value={form.category}
                onChange={(e) =>
                  setForm({
                    ...form,
                    category: e.target.value as ExpenseCategory,
                  })
                }
              >
                {options.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.label}
                    {c.archived ? " (archived)" : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Day of month">
              <input
                className="input tabular-nums"
                type="number"
                min="1"
                max="31"
                required
                value={form.day_of_month}
                onChange={(e) =>
                  setForm({ ...form, day_of_month: e.target.value })
                }
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount">
              <input
                className="input tabular-nums"
                type="number"
                step="0.01"
                min="0"
                required
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
              />
            </Field>
            <Field label="Currency">
              <select
                className="input"
                value={form.currency}
                onChange={(e) =>
                  setForm({
                    ...form,
                    currency: e.target.value as ExpenseCurrency,
                  })
                }
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start month">
              <input
                className="input"
                type="month"
                required
                value={form.start_month}
                onChange={(e) =>
                  setForm({ ...form, start_month: e.target.value })
                }
              />
            </Field>
            <Field label="End month (optional)">
              <input
                className="input"
                type="month"
                value={form.end_month}
                onChange={(e) => setForm({ ...form, end_month: e.target.value })}
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-[12.5px] text-slate-300">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Active (auto-post each month)
          </label>
          {err && <div className="text-[12.5px] text-rose-300">{err}</div>}
        </form>

        <div className="flex items-center justify-between pt-4">
          <div className="flex gap-2">
            {recurring && (
              <button
                type="button"
                className="btn-danger !py-2 !px-3 !text-[13px]"
                disabled={busy}
                onClick={() => setConfirmDelete(true)}
              >
                Delete
              </button>
            )}
            {recurring && (
              <button
                type="button"
                className="btn-secondary !py-2 !px-3 !text-[13px]"
                disabled={busy}
                onClick={runNow}
                title="Post this month's row now"
              >
                Post now
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-secondary !py-2 !px-3 !text-[13px]"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="recurring-form"
              className="btn-primary !py-2 !px-3.5 !text-[13px]"
              disabled={busy}
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this recurring cost?"
        description="Months it already posted stay in the ledger — only future auto-posting stops."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={remove}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}
